import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ROLE_LABEL, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Download } from "lucide-react";

const PORTFOLIO_ROLES = ["chief","deputy_chief","head_councillor","councillor","executive_director"];

export default function Portfolios() {
  const { user } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const canOwnPortfolio = PORTFOLIO_ROLES.includes(user?.role);

  const load = () => api.get("/portfolios").then(r=>setPortfolios(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link support: /portal/portfolios?owner={id}
  useEffect(() => {
    const owner = searchParams.get("owner");
    if (owner) setSelected(owner);
  }, [searchParams]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="label-mono text-[#d4b876]">LEADERSHIP PORTFOLIOS</div>
      <h1 className="font-display text-4xl mt-2">Chief & Council portfolios</h1>
      <p className="text-white/60 mt-3 max-w-2xl">
        Public-record portfolios maintained by Chief, Deputy Chief, Head Councillor, Councillors, and the Executive Director. Every letter, project concept and communication is signed and timestamped.
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {portfolios.map(p => (
          <button key={p.id} onClick={()=>setSelected(p.id)} className="panel p-5 text-left corner-tick" data-testid={`portfolio-card-${p.id}`}>
            <div className="placeholder-box h-32 mb-4">[ insert {p.first_name.toLowerCase()} portrait ]</div>
            <div className="label-mono text-[#89b4fa]">{ROLE_LABEL[p.role]}</div>
            <div className="font-display text-xl mt-1">{p.first_name} {p.last_name}</div>
            <div className="font-mono text-[10px] text-white/40 mt-2">{p.item_count} portfolio items</div>
          </button>
        ))}
      </div>

      {selected && <PortfolioDetail id={selected} onClose={()=>{ setSelected(null); setSearchParams({}); load(); }} isOwner={canOwnPortfolio} currentUserId={user?.id} />}
    </div>
  );
}

function PortfolioDetail({ id, onClose, currentUserId }) {
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const confirm = useConfirm();
  const load = () => api.get(`/portfolios/${id}`).then(r=>setData(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const isMine = data.owner.id === currentUserId;

  const deleteItem = async (itemId) => {
    const ok = await confirm({ title: "Delete portfolio item?", destructive: true, confirmLabel: "Delete" });
    if (!ok) return;
    try {
      await api.delete(`/portfolios/items/${itemId}`);
      toast.success("Item deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto p-4 sm:p-8" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-4xl mx-auto border border-white/20 bg-[#181825] p-8">
        <div className="flex justify-between items-start">
          <div>
            <div className="label-mono text-[#89b4fa]">{ROLE_LABEL[data.owner.role]}</div>
            <h2 className="font-display text-3xl mt-1">{data.owner.first_name} {data.owner.last_name}</h2>
          </div>
          <button onClick={onClose} className="btn-cta" data-testid="close-portfolio">Close</button>
        </div>

        {isMine && (
          <button onClick={()=>setShowAdd(true)} className="btn-cta primary mt-6" data-testid="add-portfolio-item-btn">
            <Plus size={12}/> Add item
          </button>
        )}

        <div className="mt-8 space-y-3">
          {data.items.length === 0 && <div className="font-mono text-xs text-white/40">No items yet.</div>}
          {data.items.map(item => (
            <div key={item.id} className="border border-white/10 p-5" data-testid={`portfolio-item-${item.id}`}>
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="font-mono text-[10px] text-[#d4b876] tracking-widest uppercase">{item.kind}</div>
                  <div className="font-display text-xl mt-1">{item.title}</div>
                  {item.body && <div className="text-white/70 mt-2 whitespace-pre-wrap">{item.body}</div>}
                  {item.file_id && (
                    <a href={`${API}/files/${item.file_id}`} target="_blank" rel="noreferrer" className="btn-cta mt-3 inline-flex text-xs" data-testid={`portfolio-file-${item.file_id}`}>
                      <Download size={12}/> Attached file
                    </a>
                  )}
                </div>
                {isMine && (
                  <button onClick={()=>deleteItem(item.id)} className="text-[#ef5b6b] hover:text-white" data-testid={`delete-item-${item.id}`}>
                    <Trash2 size={16}/>
                  </button>
                )}
              </div>
              <div className="font-mono text-[10px] text-white/30 mt-2">{new Date(item.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>

        {showAdd && <AddItemModal onClose={()=>setShowAdd(false)} onCreated={load} />}
      </div>
    </div>
  );
}

function AddItemModal({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", kind: "note", body: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      let file_id = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
        file_id = res.data.id;
      }
      await api.post("/portfolios/items", { ...f, file_id });
      toast.success("Portfolio item published");
      onCreated(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg border border-white/20 bg-[#181825] p-6 space-y-4">
        <div className="label-mono text-[#d4b876]">NEW PORTFOLIO ITEM</div>
        <select value={f.kind} onChange={e=>setF({...f, kind:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="new-item-kind">
          <option value="letter">Letter</option>
          <option value="communication">Communication</option>
          <option value="project">Project Concept</option>
          <option value="note">Note</option>
          <option value="file">File Only</option>
        </select>
        <input placeholder="Title" required value={f.title} onChange={e=>setF({...f, title:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="new-item-title" />
        <textarea placeholder="Body / description (optional)" rows={5} value={f.body} onChange={e=>setF({...f, body:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="new-item-body" />
        <div>
          <div className="label-mono mb-2">Attach file (optional)</div>
          <input type="file" onChange={e=>setFile(e.target.files?.[0] || null)} className="font-mono text-xs text-white/70" data-testid="new-item-file" />
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={saving} className="btn-cta primary" data-testid="submit-item-btn">{saving?"Uploading...":"Publish"}</button>
        </div>
      </form>
    </div>
  );
}
